import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import { DynamicCreditCardIcons } from '@/core/components/CreditCard/dynamic-creditcard-icons.component'
import { renderFieldRows } from '@/core/components/Fields-input/Fields-renderer/checkout-field-renderer.component'
import { UInput } from '@/core/components/Fields-input/UInput.component'
import { ModalExpSelect } from '@/core/components/Fields-input/modal-exp-select.component'
import { detectCardBrand, formatCardNumber } from '@/core/utils'
import { omanCountryListAtom, omanCartHasSubscriptionAtom, omanSelectedShippingMethodAtom } from '../../../stores/oman.store'
import { omanConfig, billingAddressFields } from '../../../oman.config'
import { useRegionsByCountry } from '../../../hooks/useRegionsByCountry'
import BillingSummary from './BillingSummary'
import type { TOmanCreditCardForm, TOmanCountryItem } from '../../../types/oman.types'
import type { CheckboxProps } from '@mui/material/Checkbox'

const getCheckboxInputTestProps = (testId: string) =>
    ({
        'data-testid': testId
    }) as NonNullable<CheckboxProps['slotProps']>['input']

const getCountryName = (item: TOmanCountryItem, lang: string = omanConfig.defaultLanguage): string =>
    lang === 'ar' && item.name_native ? item.name_native : item.name_english

const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))
const currentYear = new Date().getFullYear()
const YEARS = Array.from({ length: 12 }, (_, i) => String(currentYear + i))
const creditCardInputSx = {
    '& .MuiInputBase-root': {
        backgroundColor: '#FFFFFF'
    }
}

interface AddCreditCardProps {
    value: TOmanCreditCardForm
    onChange: (form: TOmanCreditCardForm) => void
    errors?: Record<string, string>
}

const AddCreditCard = ({ value, onChange, errors = {} }: AddCreditCardProps) => {
    const { translate } = useTranslate()
    const countryList = useAtomValue(omanCountryListAtom)
    const hasSubscription = useAtomValue(omanCartHasSubscriptionAtom)
    const selectedShippingMethod = useAtomValue(omanSelectedShippingMethodAtom)
    const { regions, regionsLoading } = useRegionsByCountry(value.billing.country || 'OM')
    const isPickup = selectedShippingMethod === 'pickup'

    const set = <K extends keyof TOmanCreditCardForm>(key: K, val: TOmanCreditCardForm[K]) => onChange({ ...value, [key]: val })

    const setBilling = (key: keyof TOmanCreditCardForm['billing'], val: string) => onChange({ ...value, billing: { ...value.billing, [key]: val } })

    const brand = detectCardBrand(value.cardNumber)

    return (
        <Stack spacing={2} sx={{ width: '100%' }} data-testid="add-credit-card-stack-uinput-1">
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: '100%' }} data-testid="add-credit-card-stack-uinput-2">
                <Box sx={{ flex: { md: 2 }, width: '100%' }} data-testid="add-credit-card-box-uinput">
                    <UInput
                        value={value.cardNumber}
                        onChange={e => set('cardNumber', formatCardNumber(e.target.value))}
                        fullWidth
                        required
                        error={!!errors.cardNumber}
                        helperText={errors.cardNumber}
                        slotProps={{
                            htmlInput: {
                                inputMode: 'numeric',
                                maxLength: 19,
                                'data-testid': 'add-credit-card-card-number-input'
                            }
                        }}
                        InputProps={{
                            endAdornment: brand ? (
                                <Box sx={{ pr: 1, display: 'flex', alignItems: 'center' }} data-testid="add-credit-card-box-dynamic-credit-card-icons">
                                    <DynamicCreditCardIcons paymentOptions={brand} height={20} data-testid="add-credit-card-dynamic-credit-card-icons" />
                                </Box>
                            ) : undefined
                        }}
                        sx={creditCardInputSx}
                        data-testid="add-credit-card-uinput"
                    />
                </Box>
                <Box sx={{ flex: { md: 1 }, width: '100%' }} data-testid="add-credit-card-box-modal-exp-select">
                    <ModalExpSelect
                        value={{ month: value.expMonth, year: value.expYear ? `20${value.expYear}`.slice(-4) : '' }}
                        options={{ month: MONTHS, year: YEARS }}
                        onSet={(month, year) => {
                            onChange({ ...value, expMonth: month, expYear: year.slice(2) })
                        }}
                        error={!!(errors.expMonth || errors.expYear)}
                        helperText={errors.expMonth || errors.expYear}
                        inputTestId="add-credit-card-expiration-input"
                        sx={creditCardInputSx}
                        data-testid="add-credit-card-modal-exp-select"
                    />
                </Box>
                <Box sx={{ flex: { md: 1 }, width: '100%' }} data-testid="add-credit-card-box-uinput-password">
                    <UInput
                        value={value.cvv}
                        onChange={e => set('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                        fullWidth
                        required
                        type="password"
                        error={!!errors.cvv}
                        helperText={errors.cvv}
                        slotProps={{
                            htmlInput: {
                                inputMode: 'numeric',
                                maxLength: 4,
                                'data-testid': 'add-credit-card-cvv-input'
                            }
                        }}
                        sx={creditCardInputSx}
                        data-testid="add-credit-card-uinput-password"
                    />
                </Box>
            </Stack>
            <FormControlLabel
                sx={{ '&.MuiFormControlLabel-root': { mt: '24px', mb: '24px', alignItems: 'flex-start' } }}
                control={
                    <Checkbox
                        checked={value.saveCard === true || hasSubscription === true}
                        onChange={e => set('saveCard', e.target.checked)}
                        disabled={hasSubscription}
                        disableRipple
                        sx={{
                            p: 0,
                            mr: 1,
                            mt: '1px',
                            color: '#1976D2',
                            '&.Mui-checked': { color: '#1976D2' },
                            '&.Mui-disabled': {
                                cursor: 'not-allowed',
                                backgroundColor: 'transparent !important',
                                color: 'rgba(0, 0, 0, 0.26) !important'
                            },
                            '&:hover, &.Mui-disabled:hover': {
                                backgroundColor: 'transparent !important'
                            }
                        }}
                        slotProps={{
                            input: getCheckboxInputTestProps('add-credit-card-save-card-input')
                        }}
                        data-testid="add-credit-card-save-card-checkbox"
                    />
                }
                label={
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 16, fontWeight: 500 }}
                        data-testid="add-credit-card-typography-save-credit-card-with-description">
                        {translate('save_credit_card_with_description')}
                    </Typography>
                }
                data-testid="add-credit-card-form-control-label-1"
            />
            <Divider data-testid="add-credit-card-divider" />
            {!isPickup && (
                <FormControlLabel
                    sx={{ '&.MuiFormControlLabel-root': { mt: '24px' } }}
                    control={
                        <Checkbox
                            checked={value.billingSameAsShipping}
                            onChange={e => set('billingSameAsShipping', e.target.checked)}
                            disableRipple
                            sx={{
                                p: 0,
                                mr: 1,
                                color: '#1976D2',
                                '&.Mui-checked': { color: '#1976D2' },
                                '&:hover': { backgroundColor: 'transparent' },
                                '&:active': { backgroundColor: 'transparent' }
                            }}
                            slotProps={{
                                input: getCheckboxInputTestProps('add-credit-card-billing-same-as-shipping-input')
                            }}
                            data-testid="add-credit-card-billing-same-as-shipping-checkbox"
                        />
                    }
                    label={
                        <Typography
                            variant="body2"
                            sx={{ fontSize: 16, fontWeight: 500 }}
                            data-testid="add-credit-card-typography-billing-address-same-shipping">
                            {translate('billing_address_same_shipping')}
                        </Typography>
                    }
                    data-testid="add-credit-card-form-control-label-2"
                />
            )}
            {!isPickup && value.billingSameAsShipping && <BillingSummary data-testid="add-credit-card-billing-summary" />}
            {(isPickup || !value.billingSameAsShipping) && (
                <Stack spacing={2} sx={{ width: '100%' }} data-testid="add-credit-card-stack">
                    {renderFieldRows(billingAddressFields, {
                        form: value.billing as unknown as Record<string, unknown>,
                        setForm: updater => {
                            const nextBilling =
                                typeof updater === 'function'
                                    ? (updater as (prev: Record<string, unknown>) => Record<string, unknown>)(
                                          value.billing as unknown as Record<string, unknown>
                                      )
                                    : updater
                            const nextCountry = String(nextBilling.country ?? value.billing.country)
                            const billing = {
                                ...nextBilling,
                                state: nextCountry === value.billing.country ? nextBilling.state : ''
                            } as TOmanCreditCardForm['billing']
                            onChange({ ...value, billing })
                        },
                        set: key => e => setBilling(key as keyof TOmanCreditCardForm['billing'], e.target.value),
                        formDisabled: false,
                        countryList,
                        getCountryDisplayName: item => getCountryName(item as TOmanCountryItem),
                        regions: regions.map(r => ({ name: r.name, id: r.value })),
                        regionsLoading,
                        errors,
                        errorKeyPrefix: 'billing.',
                        testIdPrefix: 'add-credit-card-billing'
                    })}
                </Stack>
            )}
        </Stack>
    )
}

export default AddCreditCard
